/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef } from 'react';
import { differenceInYears, parseISO } from 'date-fns';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { UpgradePlanModal } from '../UpgradePlanModal';
import { useNavigate } from 'react-router-dom';
import StepContainer from '../StepContainer';
import { PrecoDoRodape } from '../TermosAssinatura';
import type { RodapeDoPreco } from '../../utils/termos-assinatura';

import toast from 'react-hot-toast';

export interface Child {
  id?: string;
  name: string;
  birthDate: string;
  gender: 'Masculino' | 'Feminino' | '';
}

interface StepTwoProps {
  onSubmitChildren: (children: Child[]) => void;
  initialChildren?: Child[];
  onBack: () => void;
  price: {
    total: string;
    cupom: any;
    /** Valor + frequência/total da barra do celular (utils/termos-assinatura). */
    rodape?: RodapeDoPreco;
  };
  planoSelecionado: {
    name: string;
    price: number;
  };
  /** Quanto o Explorador custa a mais, na unidade de cobrança (null = não afirma valor). */
  diferencaUpgrade?: string | null;
}

const StepTwo: React.FC<StepTwoProps> = ({
  onSubmitChildren,
  initialChildren = [],
  onBack,
  price,
  planoSelecionado,
  diferencaUpgrade = null,
}) => {
  const navigate = useNavigate();

  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const isSubmittingRef = useRef(false); // 🔒 Proteção contra duplo clique
  const [children, setChildren] = useState<Child[]>(initialChildren || []);
  const [child, setChild] = useState<Child>({
    name: '',
    birthDate: '',
    gender: '',
  });
  const [isFormVisible, setIsFormVisible] = useState(initialChildren?.length ? initialChildren.length < 2 : true);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{
    name?: string;
    birthDate?: string;
    gender?: string;
  }>({});
  const [touched, setTouched] = useState<{
    name: boolean;
    birthDate: boolean;
    gender: boolean;
  }>({
    name: false,
    birthDate: false,
    gender: false,
  });
  const location = useLocation();
  const plano = location.pathname.split('/')[1]?.split('-')[0];

  const validateChildField = (field: string, value: string): string | undefined => {
    switch (field) {
      case 'name':
        if (!value.trim()) return 'Nome é obrigatório';
        if (value.trim().length < 3) return 'Nome deve ter pelo menos 3 caracteres';
        if (!/^[a-zA-ZÀ-ÿ\s]+$/.test(value.trim())) return 'Nome deve conter apenas letras';
        break;
      case 'birthDate': {
        if (!value) return 'Data de nascimento é obrigatória';

        const age = calculateAge(value);
        if (age === null) {
          return 'Data de nascimento inválida';
        }
        // Permitir datas futuras (até 9 meses) — mães grávidas
        if (age < 0) {
          const birth = new Date(value);
          const maxFuture = new Date();
          maxFuture.setMonth(maxFuture.getMonth() + 9);
          if (birth > maxFuture) {
            return 'Data prevista não pode ser mais de 9 meses no futuro';
          }
        } else if (age > 12) {
          return 'A criança deve ter no máximo 12 anos';
        }
        break;
      }
      case 'gender':
        if (!value) return 'Gênero é obrigatório';
        break;
    }
    return undefined;
  };

  const handleFieldChange = (field: string, value: string) => {
    const error = validateChildField(field, value);
    setFieldErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handleFieldBlur = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const value = field === 'name' ? child.name : field === 'birthDate' ? child.birthDate : child.gender;
    const error = validateChildField(field, value);
    setFieldErrors((prev) => ({ ...prev, [field]: error }));
  };

  const getFieldError = (field: string): string | undefined => {
    return touched[field as keyof typeof touched] ? fieldErrors[field as keyof typeof fieldErrors] : undefined;
  };

  const getFieldClassName = (field: string): string => {
    const hasError = getFieldError(field);
    const isTouched = touched[field as keyof typeof touched];
    const baseClass =
      field === 'gender'
        ? 'border px-4 py-3 rounded-lg h-12 appearance-none bg-white'
        : 'border px-4 py-4 rounded-lg h-12';

    if (hasError && isTouched) {
      return `${baseClass} border-red-500 bg-red-50`;
    } else if (isTouched && !hasError) {
      return `${baseClass} border-green-500 bg-green-50`;
    }
    return `${baseClass} border-[#dadae5]`;
  };

  const handleAddOrUpdateChild = () => {
    // Validar todos os campos
    const nameError = validateChildField('name', child.name);
    const birthDateError = validateChildField('birthDate', child.birthDate);
    const genderError = validateChildField('gender', child.gender);

    if (nameError || birthDateError || genderError) {
      setFieldErrors({
        name: nameError,
        birthDate: birthDateError,
        gender: genderError,
      });
      setTouched({ name: true, birthDate: true, gender: true });
      return;
    }

    const updatedChildren =
      editingIndex !== null ? children.map((c, i) => (i === editingIndex ? child : c)) : [...children, child];

    setChildren(updatedChildren);
    setChild({ name: '', birthDate: '', gender: '' });
    setEditingIndex(null);
    setIsFormVisible(false);
    setFieldErrors({});
    setTouched({ name: false, birthDate: false, gender: false });
  };

  const handleEdit = (index: number) => {
    setChild(children[index]);
    setEditingIndex(index);
    setIsFormVisible(true);
  };

  const calculateAge = (birthDate: string) => {
    try {
      const birth = parseISO(birthDate);

      // Validar se a data é válida
      if (isNaN(birth.getTime())) {
        return null;
      }

      return differenceInYears(new Date(), birth);
    } catch {
      return null;
    }
  };

  const getAvatarImage = (gender: string) => {
    if (gender === 'Feminino') return '/menina.png';
    if (gender === 'Masculino') return '/menino.png';
    return '/avatar-default.png';
  };

  const isFormValid = children.length > 0;

  const ciclo = location.pathname.includes('anual');

  const handleAddChildClick = () => {
    // Se é plano descoberta e já tem 1 criança, abrir modal de upgrade
    if (plano === 'descoberta' && children.length >= 1) {
      setShowUpgradeModal(true);
      return;
    }

    // Para todos os planos, verificar se pode adicionar mais crianças
    if (children.length === 0) {
      toast.error('Complete os dados da primeira criança antes de adicionar outra.');
      return;
    }

    if (children.length < 2) {
      setChild({ name: '', birthDate: '', gender: '' });
      setEditingIndex(null);
      setIsFormVisible(true);
    }
  };

  // Atualizar children quando initialChildren mudar
  useEffect(() => {
    console.log('StepTwo - initialChildren:', initialChildren);
    console.log('StepTwo - plano:', plano);
    if (initialChildren && initialChildren.length > 0) {
      setChildren(initialChildren);
      // Não mostrar formulário se já tem crianças cadastradas
      setIsFormVisible(false);
    }
  }, [initialChildren]);

  useEffect(() => {
    if (window.fbq) {
      window.fbq('track', 'AddContactInfo', {
        content_name: planoSelecionado?.name,
        value: planoSelecionado.price.toFixed(2),
        currency: 'BRL',
      });
    }
  }, []);

  return (
    <>
      <StepContainer>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <h1 className="font-bold text-xl mb-3">Dados das crianças</h1>
          <span className="text-base text-[#595D69]">
            Digite os dados das crianças para que possamos oferecer os livros mais adequados para cada fase delas.
          </span>
        </motion.div>

        <div className="mt-10">
          <h2 className="text-base text-[#595D69]">Crianças ({children.length})</h2>
          <div className="w-full py-2 border-b border-black/10" />
        </div>

        {/* Lista de crianças cadastradas */}
        {children.map((c, i) => (
          <div
            key={i}
            className="flex items-center justify-between p-4 border border-black/10 rounded-lg my-3 bg-white shadow-sm"
          >
            <div className="flex items-center gap-4">
              <img src={getAvatarImage(c.gender)} className="w-12 h-12 rounded-full" alt="Avatar" />
              <div>
                <p className="font-medium text-gray-800">{c.name}</p>
                <p className="text-gray-500 text-sm">Idade: {calculateAge(c.birthDate)} anos</p>
              </div>
            </div>
            <button
              onClick={() => handleEdit(i)}
              className="border border-[#018ED5] text-[#018ED5] px-2 lg:px-4 py-2 rounded hover:opacity-90"
            >
              Editar
            </button>
          </div>
        ))}

        {/* Formulário de nova criança ou edição */}
        {isFormVisible && (
          <div className="border border-black/10 rounded-md p-4 my-5 bg-white">
            <div className="flex flex-col gap-2">
              <label className="text-base text-[#595D69]">Nome Completo</label>
              <input
                type="text"
                className={getFieldClassName('name')}
                placeholder="Nome e Sobrenome"
                value={child.name}
                onChange={(e) => {
                  setChild({ ...child, name: e.target.value });
                  handleFieldChange('name', e.target.value);
                }}
                onBlur={() => handleFieldBlur('name')}
                aria-describedby={getFieldError('name') ? 'child-name-error' : undefined}
                aria-invalid={!!getFieldError('name')}
              />
              {getFieldError('name') && (
                <p id="child-name-error" className="text-red-500 text-sm mt-1" role="alert">
                  {getFieldError('name')}
                </p>
              )}
            </div>

            <div className="flex items-center justify-between gap-4 mt-4 w-full">
              <div className="flex flex-col gap-2 w-full">
                <label className="text-base text-[#595D69]">Data de nascimento</label>
                <input
                  type="date"
                  className={getFieldClassName('birthDate')}
                  value={child.birthDate}
                  onChange={(e) => {
                    setChild({ ...child, birthDate: e.target.value });
                    handleFieldChange('birthDate', e.target.value);
                  }}
                  onBlur={() => handleFieldBlur('birthDate')}
                  aria-describedby={getFieldError('birthDate') ? 'child-birthdate-error' : undefined}
                  aria-invalid={!!getFieldError('birthDate')}
                />
                {getFieldError('birthDate') && (
                  <p id="child-birthdate-error" className="text-red-500 text-sm mt-1" role="alert">
                    {getFieldError('birthDate')}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-2 w-full">
                <label className="text-base text-[#595D69]">Gênero</label>
                <select
                  className={getFieldClassName('gender')}
                  value={child.gender}
                  onChange={(e) => {
                    setChild({
                      ...child,
                      gender: e.target.value as Child['gender'],
                    });
                    handleFieldChange('gender', e.target.value);
                  }}
                  onBlur={() => handleFieldBlur('gender')}
                  aria-describedby={getFieldError('gender') ? 'child-gender-error' : undefined}
                  aria-invalid={!!getFieldError('gender')}
                >
                  <option value="">Selecione</option>
                  <option value="Masculino">Masculino</option>
                  <option value="Feminino">Feminino</option>
                </select>
                {getFieldError('gender') && (
                  <p id="child-gender-error" className="text-red-500 text-sm mt-1" role="alert">
                    {getFieldError('gender')}
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end mt-5 gap-3">
              {editingIndex !== null && (
                <button
                  onClick={() => {
                    setChild({ name: '', birthDate: '', gender: '' });
                    setEditingIndex(null);
                    setIsFormVisible(false);
                  }}
                  className="text-[#018ED5] border border-[#018ED5] px-6 py-2 rounded hover:opacity-90"
                >
                  Cancelar edição
                </button>
              )}
              <button
                onClick={handleAddOrUpdateChild}
                className="bg-[#018ED5] text-white px-6 py-2 rounded hover:opacity-90"
              >
                {editingIndex !== null ? 'Salvar edição' : 'Salvar'}
              </button>
            </div>
          </div>
        )}

        {/* Botão para adicionar nova criança */}
        <div className="flex justify-center mb-8">
          <button
            onClick={handleAddChildClick}
            disabled={children.length >= 2 || (plano === 'descoberta' && children.length >= 1)}
            className={`flex items-center gap-2 px-4 py-2 rounded ${
              children.length >= 2 || (plano === 'descoberta' && children.length >= 1)
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-[#018ED5] hover:opacity-80'
            }`}
          >
            <img src="/icon-plus-blue.svg" alt="" />
            Adicionar criança
          </button>
        </div>

        <div className="flex items-center justify-between">
          <button
            onClick={onBack}
            className="flex gap-2 hover:cursor-pointer items-center hover:opacity-90 text-[#9195A1] px-3 py-2 rounded-md"
          >
            <img src="/arrow-left.svg" alt="" /> Voltar
          </button>
          <button
            disabled={!isFormValid || loading}
            onClick={async () => {
              // 🔒 Proteção contra duplo clique ANTES do React atualizar
              if (isSubmittingRef.current) {
                console.warn('⚠️ Duplo clique bloqueado!');
                return;
              }

              isSubmittingRef.current = true;
              setLoading(true);
              window.scrollTo({ top: 0, behavior: 'smooth' });
              try {
                await onSubmitChildren(children);
              } finally {
                setLoading(false);
                isSubmittingRef.current = false;
              }
            }}
            className={`text-white lg:flex hidden px-3 py-2 rounded-md items-center justify-center gap-2 ${
              isFormValid && !loading
                ? 'bg-[#018ED5] hover:opacity-90 hover:cursor-pointer'
                : 'bg-[#dadae5] cursor-not-allowed'
            }`}
          >
            {loading && (
              <svg
                className="animate-spin h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
            )}
            {loading ? 'Salvando...' : 'Continuar'}
          </button>
        </div>
      </StepContainer>

      {showUpgradeModal && (
        <UpgradePlanModal
          extraPrice={diferencaUpgrade}
          onClose={() => setShowUpgradeModal(false)}
          onConfirm={() => {
            const isCiclo = ciclo ? 'anual' : 'mensal';
            navigate(`/explorador-${isCiclo}`);
            setShowUpgradeModal(false);
          }}
        />
      )}

      <div className="w-full lg:hidden flex items-center justify-between gap-3 px-6 py-2 left-0 min-h-[80px] z-50 bg-white border-t border-black/10 fixed bottom-0">
        <div className="min-w-0 flex-1">
          <span>
            {price.cupom && (
              <>
                <img src="/icon-tag.svg" className="w-4 h-4" /> {price.cupom.code}
              </>
            )}
          </span>
          {price.rodape ? (
            <PrecoDoRodape rodape={price.rodape} />
          ) : (
            <h3 className="font-bold text-3xl">{price.total}</h3>
          )}
          <h2 className="font-bold text-xs">+ FRETE GRÁTIS</h2>
        </div>
        <div className="shrink-0 max-w-[50%]">
          <button
            onClick={async () => {
              // 🔒 Proteção contra duplo clique ANTES do React atualizar
              if (isSubmittingRef.current) {
                console.warn('⚠️ Duplo clique bloqueado!');
                return;
              }

              isSubmittingRef.current = true;
              setLoading(true);
              window.scrollTo({ top: 0, behavior: 'smooth' });
              try {
                await onSubmitChildren(children);
              } finally {
                setLoading(false);
                isSubmittingRef.current = false;
              }
            }}
            className={`text-white px-4 py-4 text-center leading-tight rounded-md flex items-center justify-center gap-2 ${
              isFormValid && !loading
                ? 'bg-[#018ED5] hover:opacity-90 hover:cursor-pointer'
                : 'bg-[#dadae5] cursor-not-allowed'
            }`}
          >
            {loading && (
              <svg
                className="animate-spin h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
            )}
            {loading ? 'Salvando...' : 'Continuar'}
          </button>
        </div>
      </div>
    </>
  );
};

export default StepTwo;
